// src/lib/intake/intakeConfig.js
//
// Ticket 061 (Advisor Intake I4). The Section 1 screen list and the few
// intake-only constants. Option lists and the move-date placeholder come from
// src/lib/sharedOptions.js (generated); question wording for citizenship,
// region, marital status and asset split comes from slotConfig.js. Nothing
// here redefines an option list.
//
// YEAR MATH lives here once:
//   currentYear(today)  - the year the screen is read in (UTC, like the server window)
//   previousYear(today) - currentYear - 1
//   intakeBaseYears     - the tax-year window the intake offers (Snapshot rule):
//                         the prior year only through 30 June, then current, next
//   moveCutoffYear      - the cutoff year C = the OLDEST year in that window
//                         (round 2, John 2026-11-01). It moves on 1 July.

import {
  COMUNIDAD_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  CITIZENSHIP_STATUS_OPTIONS,
  ASSET_SPLIT_OPTIONS,
  AGE_BAND_OPTIONS,
  FORAL_COMUNIDAD_OPTIONS,
  MAJOR_EVENT_OPTIONS,
  EMPLOYMENT_WORK_LOCATION_OPTIONS,
  BECKHAM_STATUS_OPTIONS,
  PROPERTY_LOCATION_OPTIONS,
  COUNTRY_OPTIONS,
  MAX_INCOME_ITEMS,
  NET_WORTH_BAND_OPTIONS,
  FOREIGN_ACCOUNT_BAND_OPTIONS,
  INTAKE_SHARE_CONSENT_TEMPLATE,
} from '../sharedOptions'
import { slots } from '../slotConfig'

export function currentYear(today = new Date()) {
  return today.getUTCFullYear()
}

export function previousYear(today = new Date()) {
  return currentYear(today) - 1
}

// The single source for the offered tax years (before any move-date filter).
export function intakeBaseYears(today = new Date()) {
  const month = today.getUTCMonth()      // 0 = January
  const day = today.getUTCDate()
  const priorYearIsOpen = month < 5 || (month === 5 && day <= 30)   // through 30 June
  return [
    ...(priorYearIsOpen ? [previousYear(today)] : []),
    currentYear(today),
    currentYear(today) + 1,
  ]
}

// Upper bound for a typed move year: C + 10 (round 3). The server's tax-year
// window is only currentYear-2..currentYear+1, so a move date further out simply
// gives an empty tax-year list; ten years is a sensible limit for a planned move
// and rules out typos such as 20266 or 9999.
export const MOVE_YEAR_SPAN = 10

export function moveMaxYear(today = new Date()) {
  return moveCutoffYear(today) + MOVE_YEAR_SPAN
}

export function moveCutoffYear(today = new Date()) {
  return intakeBaseYears(today)[0]
}

// ── Screen order ─────────────────────────────────────────────────────────────
// Ids 1.1 to 1.7 are the wireflow keys (tickets/053-intakedesign/01_wireflow.md).
// ON-SCREEN ORDER (D-OD5): the move-date screen comes first, then the tax year,
// because the year list depends on the move date.
//   1.2 move date   1.1 tax year   1.3 region   1.4 citizenship
//   1.5 marital     1.6 age        1.7 people you support
//   5.1 filing history (ticket 066: last Section 1 screen, so the email gate follows it)
export const SECTION1_SCREEN_ORDER = ['1.2', '1.1', '1.3', '1.4', '1.5', '1.6', '1.7', '5.1']
export const MOVE_SCREEN = '1.2'
export const TAX_YEAR_SCREEN = '1.1'

// not_married is the Calculator's coarse answer; the intake shows the five real
// choices (D-OD7).
export const EXCLUDED_MARITAL_VALUES = ['not_married']

export const MARITAL_CHOICES = MARITAL_STATUS_OPTIONS.filter((o) => !EXCLUDED_MARITAL_VALUES.includes(o.value))

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

// Wording switches. No Section 1 question can be skipped (John, post-testing).
export const NOT_SURE_LABEL = 'Not sure'
export const NONE_LABEL = 'None'

// Screen text. Every screen must be answered; there is no Skip anywhere.
export const SECTION1_SCREENS = {
  '1.2': {
    title: 'When did you move to Spain?',
    hint: 'Choose one. If you moved here earlier, no date is needed. Otherwise enter the full date you arrived, or plan to (a planned move is fine).',
    keys: ['move_date'],
  },
  '1.1': {
    title: 'Which tax year is this for?',
    hint: 'Choose the year you want your advisor to look at.',
    keys: [],
  },
  '1.3': {
    title: slots.comunidad_autonoma.label,
    hint: slots.comunidad_autonoma.hint,
    keys: ['comunidad_autonoma'],
  },
  '1.4': {
    title: slots.citizenship_status.label,
    hint: slots.citizenship_status.hint,
    keys: ['citizenship_status', 'eu_citizen'],
  },
  '1.5': {
    title: slots.marital_status.label,
    hint: slots.marital_status.hint,
    keys: ['marital_status', 'asset_split', 'spouse_citizenship_status', 'spouse_eu_citizen'],
  },
  '1.6': {
    title: 'Which age bracket are you in?',
    hint: 'Bands only. We never ask for a birth date.',
    keys: ['age_band', 'spouse_age_band'],
  },
  '1.7': {
    title: 'Who do you support?',
    hint: 'Children and relatives who depend on you can lower your tax. Choose "None" if there are none.',
    keys: ['children_under_25', 'children_under_3', 'ascendants_over_65', 'ascendants_over_75', 'disability_taxpayer'],
  },
  '5.1': {
    title: slots.filed_spain_before.label,
    hint: 'Required. Your advisor needs to know whether this is your first Spanish return.',
    keys: ['filed_spain_before', 'has_gestor'],
  },
}

// Filing history (5.1): the two yes/no questions. Labels come from slotConfig.
export const FILING_OPTIONS = slots.filed_spain_before.options.map((o) => ({ value: o.value, label: o.label }))
export const GESTOR_QUESTION = slots.has_gestor.label
export const GESTOR_OPTIONS = slots.has_gestor.options.map((o) => ({ value: o.value, label: o.label }))

// Questions inside screens, by fact key.
export const FOLLOW_UP_TEXT = {
  eu_citizen: { question: slots.eu_citizen.label, hint: slots.eu_citizen.hint },
  asset_split: { question: slots.asset_split.label, hint: slots.asset_split.hint },
  spouse_citizenship_status: { question: "Spouse's citizenship status" },
  spouse_eu_citizen: { question: 'Does your spouse also hold US citizenship or a Green Card?', hint: slots.eu_citizen.hint },
  spouse_age_band: { question: "Spouse's age" },
}

export const EU_FOLLOW_UP_OPTIONS = slots.eu_citizen.options.map((o) => ({ value: o.value, label: o.label }))

export const COUNT_ROWS = [
  { key: 'children_under_25', label: 'Children under 25', whole: null },
  { key: 'children_under_3', label: '...how many are under 3?', whole: 'children_under_25' },
  { key: 'ascendants_over_65', label: 'Relatives aged 65 or over who depend on you', whole: null },
  { key: 'ascendants_over_75', label: '...how many are 75 or over?', whole: 'ascendants_over_65' },
]

export const DISABILITY_LABEL = 'Do you have a recognized disability of 33% or higher?'

// Move-date screen text: the two checkboxes. {C} is the cutoff year.
export function moveBeforeLabel(today = new Date()) {
  return `Lived here since before ${moveCutoffYear(today)}`
}

export function moveRecentLabel(today = new Date()) {
  return `Moved here ${moveCutoffYear(today)} or after`
}

export const FORAL_NOTICE = (label) =>
  `${label} is a foral territory with its own tax authority and rates, separate from the national system. Your advisor will take this into account. You can keep going.`

// ── Sections 2 and 3 (ticket 064, Advisor Intake I5; AMENDMENTS 1 and 2) ──────
// Screen ids are the wireflow keys (tickets/053-intakedesign). Order on screen:
//   "Your money"   2.1 events + owned property (Spain / Outside Spain + countries)
//                  2.2 ALL of your income cards (suggested first)
//                  2.3 spouse gate   2.3s the spouse's cards on one page
//                  2.4 autónomo details   2.5 Beckham (its own page)
//                  3.3 where a sold property was   3.4 nudge check
//                  4.1 net worth and accounts outside Spain (ticket 066)
// (3.1, 3.1s and 3.2 no longer exist: income pages follow the Calculator model and
// property ownership moved into 2.1.) A screen may be hidden for a given person
// (see section23Facts.shouldShowScreen). No Skip and no "Not sure" anywhere except
// the one named exception: "Skip income for now" on 2.2 (amendment 2, Q4).
export const SECTION23_SCREEN_ORDER = ['2.1', '2.2', '2.3', '2.3s', '2.4', '2.5', '3.3', '3.4', '4.1']
export const SECTION2_FIRST_SCREEN = '2.1'

// Plain section names (John). 3.3, 3.4 and 4.1 belong to "Your money" (ticket 066
// renamed the section title; the 2.2 SCREEN title "Your income" is unchanged).
export const SECTION_NAMES = { 1: 'Your info', 2: 'Your money' }

// Which section a screen belongs to (1 or 2) for the step label and progress.
export function sectionOfScreen(id) {
  return SECTION1_SCREEN_ORDER.includes(id) ? 1 : 2
}

// The grouped events checklist (amendment 2). Event values and labels come from
// MAJOR_EVENT_OPTIONS (sharedOptions); this only chooses groups and order.
// 'owned_property' is the one line that is not an event fact: it reveals the
// Spain / Outside Spain follow-up and is stored in the property domain.
export const OWNED_PROPERTY_LINE = 'owned_property'
export const EVENT_GROUPS = [
  { label: 'Work & Business', lines: ['worked_for_employer', 'self_employed', 'owned_business'] },
  { label: 'Property', lines: [OWNED_PROPERTY_LINE, 'received_rental_income', 'sold_property'] },
  { label: 'Investments & Retirement', lines: ['received_dividends_interest', 'large_investment_sale', 'sold_crypto', 'rsu_or_options', 'received_pension_income', 'retirement_withdrawal'] },
  { label: 'Other Life Events', lines: ['inheritance_or_gift', 'made_significant_gift'] },
]
export const OWNED_PROPERTY_LABEL = 'Owned property'
export const OWNED_SPAIN_LABEL = 'Spain'
export const OWNED_OUTSIDE_LABEL = 'Outside Spain'
export const OWNED_PROPERTY_QUESTION = 'Where was the property?'
export const OWNED_COUNTRIES_QUESTION = 'Which countries?'
export const OWNED_INCOMPLETE_HINT = 'Choose Spain, Outside Spain or both. For Outside Spain, pick at least one country.'
export const RENTAL_PROPERTY_PROMPT = 'You said you received rental income. If you owned that property, tick "Owned property" above.'

// Every income card group, on one page (all four, in the Calculator's order).
export const ALL_GROUP_KEYS = ['work_business', 'retirement', 'investments_property', 'other']

export const MAX_ITEMS = MAX_INCOME_ITEMS
export const ITEM_COUNTER_FROM = 15
export const LIMIT_NOTICE = `You have reached the limit of ${MAX_INCOME_ITEMS} income items. Remove one to add another. If you really have more, tell your advisor.`
export const EMPTY_CARDS_REMOVED = (n) =>
  `We removed ${n} empty card${n === 1 ? '' : 's'}. Cards you did not fill in are not saved.`
export const SAVE_FAILED_NOTICE = 'We could not save this screen. Your answers are still here. Check your connection and press Next to try again.'

export const SECTION23_SCREENS = {
  '2.1': {
    title: 'What applied to you or your household this year?',
    hint: 'Tick everything that applies to the tax year you chose. Choose "None of these" if nothing did.',
  },
  '2.2': {
    title: 'Your income',
    hint: 'Add each kind of income you had this year. We list the ones that look relevant first. Remove cards you do not need; a card you filled in with 0 is saved as zero. Or skip income for now.',
    none: 'I had no income this year',
    skip: 'Skip income for now',
  },
  '2.3': {
    title: 'Does your spouse have income?',
    hint: 'Income from work, a business, a pension, investments or anything else, during this tax year.',
  },
  '2.3s': {
    title: "Your spouse's income",
    hint: 'Add each kind of income your spouse had this year. Remove cards you do not need; a card you filled in with 0 is saved as zero.',
  },
  '2.4': {
    title: 'Your autónomo details',
    hint: 'A few facts about your registration as self-employed in Spain.',
  },
  '2.5': {
    title: 'Are you using the Beckham law regime?',
    hint: 'The special tax regime for people who moved to Spain for work. Choose No if you are not sure it applies to you.',
  },
  '3.3': {
    title: 'Where was the property you sold?',
    hint: 'Choose the country where the property you sold was located.',
  },
  '3.4': {
    title: 'A few things to double-check',
    hint: 'You told us about these, but we did not see matching income. You can go back and add it, or carry on.',
  },
  '4.1': {
    title: 'Your net worth and accounts outside Spain',
    hint: 'Both questions are required. A rough range is fine.',
  },
}

// 4.1: two required single-choice questions, each labelled with its currency.
// The band lists come from sharedOptions; neither offers "Not sure".
export const NET_WORTH_QUESTION = 'Roughly what was your worldwide net worth at the end of that tax year? (in euros)'
export const FOREIGN_ACCOUNTS_QUESTION = 'What was the highest combined balance of your accounts outside Spain this year? (in US dollars)'
export { NET_WORTH_BAND_OPTIONS, FOREIGN_ACCOUNT_BAND_OPTIONS }

// ── Review (ticket 066, screen 6.1) ───────────────────────────────────────────
export const REVIEW_SCREEN = '6.1'
export const REVIEW_STATUS_WORDS = {
  answered: 'Answered',
  not_applicable: 'Does not apply',
  none: 'None',
  open: 'Not answered yet',
  not_sure: 'Not sure',
  skipped: 'Skipped for now',
}
export const REVIEW_SKIPPED_NOTE = 'Without your income, your advisor will not see income-based findings.'
export const REVIEW_TITLE = 'Review your answers'
export const REVIEW_HINT = 'Here is what you told us. Use Edit to change an answer. Anything still open is listed first.'
export const REVIEW_NO_ISSUES = 'Nothing is missing.'
export const REVIEW_NOT_ANSWERED = 'Not answered'
export const REVIEW_SKIPPED = 'Skipped for now'
export const REVIEW_NOT_SURE = 'Not sure'
export const REVIEW_YES = 'Yes'
export const REVIEW_NO = 'No'
export const REVIEW_NONE_INCOME = 'I had no income this year'
export const REVIEW_NO_AMOUNT = 'No amount entered'
export const REVIEW_CALLOUT_TITLE = 'Worth a look'
export const REVIEW_CALLOUT_NEEDS = 'Needs an answer'
export const REVIEW_CALLOUT_UNSURE = 'Not sure or skipped'
export const REVIEW_EDIT_LABEL = 'Edit'
export const REVIEW_FIX_LABEL = 'Fix this'
export const REVIEW_END_MESSAGE = 'Your answers are saved. You can come back with the same email.'
export const REVIEW_TO_RESULTS_LABEL = 'See my results'
export const REVIEW_ROW_LABELS = {
  '1.2': 'When you moved to Spain',
  '1.1': 'Tax year',
  '1.3': 'Where you live in Spain',
  '1.4': 'Citizenship',
  '1.5': 'Marital status',
  '1.6': 'Age',
  '1.7': 'People you support',
  '5.1': 'Spanish filing history',
  '2.1': 'Events and owned property',
  '2.2': 'Your income',
  '2.3': 'Does your spouse have income?',
  '2.3s': "Your spouse's income",
  '2.4': 'Autónomo details',
  '2.5': 'Beckham law',
  '3.3': 'Where a sold property was',
  '3.4': 'Things to double-check',
  '4.1': 'Net worth and accounts outside Spain',
}
export { INTAKE_ASKED_FACTS } from './askedFacts'

export const EVENT_NONE_LABEL = 'None of these'
export const SPOUSE_HAS_INCOME_QUESTION = 'Did your spouse have income during this tax year?'
export const SPOUSE_NONE_ERROR = 'You said your spouse has income. Add an item, or go back and choose No.'
export const WORK_LOCATION_QUESTION = 'Where did you do this work?'
export const WORK_LOCATION_QUESTION_SPOUSE = 'Where did your spouse do this work?'
export const WORK_LOCATION_HINT = 'Where you were physically while you earned this income.'
export const BECKHAM_YES_NOTICE = 'We do not produce an estimate for Beckham law cases. Your advisor will handle this one.'
export const SUGGESTED_BLOCK_TITLE = 'Suggested for you'
export const OTHER_BLOCK_TITLE = 'All other income types'
export const ALL_BLOCK_TITLE = 'Income types'

export const nudgeText = (eventLabel, cardLabel) =>
  `You ticked "${eventLabel}", but we see no matching income. If it applies, add a "${cardLabel}" card.`

export {
  MAJOR_EVENT_OPTIONS,
  EMPLOYMENT_WORK_LOCATION_OPTIONS,
  BECKHAM_STATUS_OPTIONS,
  PROPERTY_LOCATION_OPTIONS,
  COUNTRY_OPTIONS,
}

export {
  COMUNIDAD_OPTIONS,
  CITIZENSHIP_STATUS_OPTIONS,
  ASSET_SPLIT_OPTIONS,
  AGE_BAND_OPTIONS,
  FORAL_COMUNIDAD_OPTIONS,
}

// ── Results (ticket 069, screen 7.1) ─────────────────────────────────────────
export const RESULTS_SCREEN = '7.1'
export const RESULTS_STEP_LABEL = 'Your results'
export const RESULTS_TITLE = 'Your results'
export const RESULTS_ESTIMATE_NOTE = 'This is an estimate based on your answers, not a tax filing.'
export const RESULTS_RATE_YEAR_LINE = (year) => `Calculated at ${year} rates.`
export const RESULTS_LOADING = 'Calculating your results…'
export const RESULTS_FAILED_TITLE = 'We could not calculate your results'
export const RESULTS_FAILED_BODY = 'Your answers are saved. Check your connection and try again.'
export const RESULTS_RETRY_LABEL = 'Try again'
export const RESULTS_ESTIMATE_HEADING = 'Your IRPF estimate'
export const RESULTS_SNAPSHOT_HEADING = 'Your Spain tax picture'
export const RESULTS_AUTONOMO_HEADING = 'Autónomo'
export const RESULTS_STANDOUT_HEADING = 'What stands out'
export const RESULTS_ESTIMATE_BLOCKED_TITLE = "We can't estimate your IRPF from these answers"
export const RESULTS_FINDINGS_STILL_APPLY = 'Your Snapshot findings in the Overall Picture tab still apply.'
export const RESULTS_NO_INCOME_REASON = 'Without an income amount we cannot estimate your IRPF.'
export const RESULTS_ENGINE_BLOCKED_REASON = 'We could not produce an IRPF estimate from these answers.'
export const RESULTS_SKIPPED_INCOME_NOTE = 'You skipped your income, so income-based findings are reduced.'
export const RESULTS_NOT_ASSESSED_TITLE = 'Not assessed'
export const RESULTS_NOT_ASSESSED_BODY = 'These results leave out what you have not answered yet. Answer them to include them.'
export const RESULTS_NOT_ASSESSED_FIX_LABEL = 'Answer this'
// What the page cannot assess while a screen is open, in plain words. Screens not
// listed use the generic line.
export const RESULTS_NOT_ASSESSED_IMPACT = {
  '4.1': 'Modelo 720 and wealth findings need your net worth and your accounts outside Spain.',
  '5.1': 'Findings about prior filings need your Spanish filing history.',
}
export const RESULTS_TAB_OVERALL = 'Overall Picture'
export const RESULTS_TAB_IRPF = 'IRPF Estimate'
export const RESULTS_STATUS_UNSAVED = 'Not saved yet'
export const RESULTS_STATUS_SAVED = 'Saved'
export const RESULTS_NOT_ASSESSED_GENERIC = 'Findings that depend on this answer are not included.'
export const RESULTS_REVIEW_LABEL = 'Review your answers'
export const RESULTS_SAVE_LABEL = 'Save my results'
export const RESULTS_SAVING_LABEL = 'Saving…'
export const RESULTS_SAVED_TEXT = 'Your results are saved. You can edit your answers and save again later.'
export const RESULTS_SAVE_FAILED = 'We could not save your results. Your answers are safe. Check your connection and try again.'
export const RESULTS_ESTIMATE_HIDDEN_NOTE = 'Your advisor will go through your numbers with you.'

// ── Share with the advisor (ticket 070, slice I8) ────────────────────────────
// All share-step wording lives here and the advisor's name arrives from the server
// (intake-share status), so a later presentation context is a copy swap. The consent
// SENTENCE is not written here: it is the shared template (sharedOptions.js, one
// definition with the server), filled with the advisor's name. The rest is product
// copy for John to approve (the stop labels, the "saving also updates" line).
export const SHARE_STEP_LABEL = 'Share your results'
export const shareConsentSentence = (advisorName) => INTAKE_SHARE_CONSENT_TEMPLATE.split('{advisor}').join(advisorName)
export const shareDateText = (iso) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
export const SHARE_ENTRY_SAVE_FIRST = (advisor) => `Save your results to share them with ${advisor}.`
export const SHARE_ENTRY_LABEL = (advisor) => `Share with ${advisor}`
export const SHARE_ENTRY_SHARED = (advisor, date) => (date ? `Shared with ${advisor} on ${date}.` : `Shared with ${advisor}.`)
export const SHARE_ENTRY_LATER_SAVES = (advisor) => `Later saves update ${advisor}.`
export const SHARE_SAVE_UPDATES_LINE = (advisor) => `Saving also updates ${advisor}.`
export const SHARE_NOT_SHARED = 'Not shared'
export const SHARE_TITLE = (advisor) => `Share your results with ${advisor}`
export const SHARE_NOTHING_YET = 'Nothing is shared until you press the button below.'
export const SHARE_SCOPE_LINE = 'You share your saved answers and results. After you share, each time you save your results they update your advisor, until you stop sharing.'
export const SHARE_NAME_LABEL = 'Your name'
export const SHARE_PHONE_LABEL = 'Phone (optional)'
export const SHARE_EMAIL_LABEL = 'Email'
export const SHARE_EMAIL_LOCKED_HINT = 'Using your account email'
export const SHARE_PRIVACY_LINK_LABEL = 'Privacy policy'
export const SHARE_SENDING_LABEL = 'Sharing…'
export const SHARE_BACK_LABEL = 'Back to my results'
export const SHARE_NAME_MAX = 100
export const SHARE_STOP_LABEL = 'Stop sharing'
export const SHARE_STOP_CONFIRM_LINE = (advisor) => `${advisor} will no longer be able to see your saved results. You can share again later.`
export const SHARE_STOP_CONFIRM_LABEL = 'Yes, stop sharing'
export const SHARE_STOP_CANCEL_LABEL = 'Keep sharing'
export const SHARE_STOPPING_LABEL = 'Stopping…'
export const SHARE_ERRORS = {
  NOT_SAVED: 'Save your latest results first, then share them.',
  REVISION_CONFLICT: 'Your answers changed. Go back to your results, save them, and try again.',
  INVALID_NAME: 'Please enter your name (up to 100 characters).',
  INVALID_PHONE: 'Please enter a valid phone number.',
  NETWORK: 'We could not reach the server. Nothing was changed. Check your connection and try again.',
  DEFAULT: 'Something went wrong. Nothing was changed. Please try again.',
}

// ── Advisor mode: an advisor enters the intake for a client they own (ticket 073) ──
// Product copy for John to approve. ADVISOR_CLIENT_REMINDER is a placeholder for Ana:
// it is shown on the New client form, nothing is stored and there is no tick-box.
export const ADVISOR_CLIENT_REMINDER = 'You are responsible for having your client’s permission to enter their information here.'
export const ADVISOR_CLIENTS_TITLE = 'My clients'
export const ADVISOR_CLIENTS_SUBTITLE = 'Clients you enter yourself. Only you can see them.'
export const ADVISOR_CLIENTS_EMPTY = 'No clients yet.'
export const ADVISOR_CLIENTS_EMPTY_SUB = 'Choose “New client” to enter your first one.'
export const ADVISOR_NEW_CLIENT_LABEL = 'New client'
export const ADVISOR_NAME_LABEL = 'Client name'
export const ADVISOR_CREATE_LABEL = 'Create'
export const ADVISOR_CREATING_LABEL = 'Creating…'
export const ADVISOR_CANCEL_LABEL = 'Cancel'
export const ADVISOR_OPEN_LABEL = 'Open'
export const ADVISOR_RENAME_LABEL = 'Rename'
export const ADVISOR_SAVE_NAME_LABEL = 'Save'
export const ADVISOR_REMOVE_LABEL = 'Remove'
export const ADVISOR_REMOVE_CONFIRM = (name) => `Remove ${name}? Their answers and saved results are deleted and cannot be brought back.`
export const ADVISOR_NO_YEARS = 'Nothing saved yet'
export const ADVISOR_CLIENTS_LOADING = 'Loading…'
export const ADVISOR_PICK_TITLE = 'Which tax year do you want to continue?'
export const ADVISOR_START_ANOTHER_YEAR = 'Start another tax year'
export const ADVISOR_RESUMING = 'Opening your client…'
export const ADVISOR_CLIENT_ERRORS = {
  INVALID_NAME: 'Please enter a name (up to 100 characters).',
  CLIENT_LIMIT: 'You have reached the limit of clients. Remove one you no longer need first.',
  NO_CLIENT: 'That client could not be found. Refresh the list.',
  NETWORK: 'We could not reach the server. Nothing was changed. Check your connection and try again.',
  DEFAULT: 'Something went wrong. Nothing was changed. Please try again.',
}
